import { featureGateValue } from '../../statsig';
import { ifInitializedValue } from './firebase';
import { clearFlagFromLocalStorage, createFlagGetter, createFlagSetter } from './localStorage';

type FlagDetails = {
    key: string;
    defaultValue?: boolean;

    /** @deprecated Using provider property when creating a flag is deprecated. The default is now 'statsig', and legacy usages of 'firebase' should be evaluated. */
    provider?: 'firebase' | 'statsig';
};
type FlagAccessor = {
    defaultValue: boolean;
    getValue: () => boolean;
    setLocalStorageValue: (value: string | number | boolean) => void;
    getLocalStorageValue: () => boolean | undefined;
    clearLocalStorageValue: () => void;
    provider?: 'firebase' | 'statsig';
};

const createFlag = ({ key: remoteKey, defaultValue = false, provider = 'statsig' }: FlagDetails): FlagAccessor => {
    const getLocalStorageValue = createFlagGetter(remoteKey);

    const getValue = (): boolean => {
        const localValue = getLocalStorageValue();

        if (localValue !== undefined) {
            return typeof localValue === 'string' ? localValue === 'true' : !!localValue;
        }

        switch (provider) {
            case 'firebase':
                return ifInitializedValue(remoteKey, defaultValue);
            case 'statsig':
                return featureGateValue(remoteKey, defaultValue);
            default:
                throw new Error(`Unknown provider: ${provider}`);
        }
    };

    return {
        getValue,
        defaultValue,
        setLocalStorageValue: createFlagSetter(remoteKey),
        getLocalStorageValue,
        clearLocalStorageValue: clearFlagFromLocalStorage(remoteKey),
        provider,
    };
};

// For info on setting up feature flags, see: docs/featureflags.md
const flags = {
    enableFlagFootball: createFlag({ key: 'tmweb_flag_football', provider: 'firebase' }),
    enableRemoveStaff: createFlag({ key: 'tmweb_enable_remove_staff', provider: 'firebase' }),
    enableVideoPlayerDebug: createFlag({ key: 'tmweb_video_player_debug', provider: 'firebase' }),
    enableTeamCreateRevamp: createFlag({ key: 'tmweb_team_create_revamp', provider: 'firebase' }),
    enableUploadedClips: createFlag({ key: 'tmweb_uploaded_clips', provider: 'firebase' }),
    enableStatsigExperiments: createFlag({
        key: 'tmweb_enable_statsig_experiments',
        defaultValue: true,
        provider: 'firebase',
    }),
    enableReportPublicResource: createFlag({
        key: 'trust_and_safety_report_from_profile',
        provider: 'statsig',
        defaultValue: true,
    }),
    enableReportLivestreamVideo: createFlag({
        key: 'trust_and_safety_report_from_livestream',
        provider: 'statsig',
        defaultValue: true,
    }),
    enableReportRecordedVideo: createFlag({
        key: 'trust_and_safety_report_from_recording',
        provider: 'statsig',
        defaultValue: true,
    }),
    enableSearchHistory: createFlag({ key: 'tmweb_enable_search_history', defaultValue: true, provider: 'firebase' }),
    enablePublicEventNavBar: createFlag({
        key: 'tmweb_enable_public_event_nav_bar',
        defaultValue: true,
        provider: 'firebase',
    }),
    enablePublicEventLineScore: createFlag({
        key: 'tmweb_enable_public_event_line_score',
        defaultValue: true,
        provider: 'firebase',
    }),
    enablePricingPageParameterization: createFlag({ key: 'enable_pricing_page_parameterization' }),
    enableTeamScheduleWidget: createFlag({
        key: 'tmweb_enable_team_schedule_widget',
        defaultValue: true,
        provider: 'firebase',
    }),
    enableWebWidgetToolsTab: createFlag({
        key: 'tmweb_enable_web_widget_tools_tab',
        defaultValue: true,
    }),
    enableTeamScheduleWidgetPromotion: createFlag({
        key: 'tmweb_enable_team_schedule_widget_promotion',
        defaultValue: true,
    }),
    enableLiveStreamPrerollAds: createFlag({
        key: 'tmweb_live_stream_preroll_ads',
        provider: 'statsig',
    }),
    excludeFreeLivestreamCopy: createFlag({
        key: 'exclude_free_livestream_copy',
        defaultValue: false,
    }),
    enableOrgAvatars: createFlag({
        key: 'enable_org_avatars',
    }),
    enableOrgBannerColor: createFlag({
        key: 'enable_org_banner_color',
    }),
    enableOrgBannerColorView: createFlag({
        key: 'enable_org_banner_color_view',
    }),
    enableDarkIndigoBanner: createFlag({
        key: 'enable_dark_indigo_banner',
        defaultValue: true,
    }),
    enableDarkIndigoLogin: createFlag({
        key: 'enable_dark_indigo_login',
    }),
    enableOrgBoxScores: createFlag({
        key: 'enable_org_box_scores',
        defaultValue: false,
    }),
    enableOrgScheduleFilter: createFlag({
        key: 'enable_org_schedule_filter',
    }),
    enableWebDisplayAds: createFlag({
        key: 'tmweb_enable_global_ad_banners',
        defaultValue: true,
    }),
    enableMaxprepsManualStatsExport: createFlag({
        key: 'maxpreps_manual_stats_export',
        provider: 'statsig',
        defaultValue: false,
    }),
    enablePlayerRemovalDialogUpdate: createFlag({
        key: 'player_removal_dialog_update',
        defaultValue: false,
    }),
    enableLineupRecommendations: createFlag({
        key: 'lineup_recommendations',
        provider: 'statsig',
        defaultValue: false,
    }),
    enableVideoErrorTracking: createFlag({
        key: 'tmweb_enable_video_error_tracking',
        defaultValue: false,
    }),
    enableFamilyTeamPassPurchase: createFlag({
        key: 'enable_family_team_pass_purchase',
        provider: 'statsig',
        defaultValue: false,
    }),
    enableDiscountCodes: createFlag({ key: 'enable_discount_codes', defaultValue: false }),
    enableBulkEditEvents: createFlag({
        key: 'bulk_edit_events_management',
    }),
    enablePreviewCreditExperience: createFlag({
        key: 'enable_preview_credit_experience',
    }),
    enableMlbRbiNgb: createFlag({
        key: 'add_mlb_rbi_to_ngb',
    }),
    enableBasketballClockScoreboard: createFlag({
        key: 'basketball_clock_scoreboard',
        provider: 'statsig',
        defaultValue: false,
    }),
    enableBasketballPlayerInsights: createFlag({
        key: 'basketball_insights_parity_gate',
        defaultValue: true,
    }),
    enableScoringAdaptiveLatency: createFlag({
        key: 'scoring_adaptive_latency',
        provider: 'statsig',
        defaultValue: false,
    }),
    enableBulkCsvEditUpload: createFlag({
        key: 'bulk_csv_edit_upload',
    }),
    enableGCHome: createFlag({
        key: 'gc_home',
        provider: 'statsig',
        defaultValue: false,
    }),
    enableFanRosterView: createFlag({
        key: 'enable_fan_roster_view',
        provider: 'statsig',
        defaultValue: false,
    }),
    enableFanPlayerProfile: createFlag({
        key: 'enable_fan_player_profile',
        provider: 'statsig',
        defaultValue: false,
    }),
    enableFanStatsTab: createFlag({
        key: 'enable_fan_stats_tab',
    }),
    enableNearMe: createFlag({ key: 'near_me_toggle' }),
    enableCoachSeasonSituationalStats: createFlag({
        key: 'enable_coach_season_situational_stats',
        provider: 'statsig',
        defaultValue: false,
    }),
    enableTeamAnnouncements: createFlag({
        key: 'team_announcements',
    }),
    enableAwsWafRequestWrapper: createFlag({
        key: 'aws_waf_request_wrapper',
        defaultValue: true,
    }),
};

type FeatureFlagNames = keyof typeof flags;
const flagKeys = Object.keys(flags) as Array<FeatureFlagNames>;

/**
 * Get the current value of either all or selected feature flags & gates.
 */
function getFlagValues(): Record<FeatureFlagNames, boolean>;
function getFlagValues<T extends FeatureFlagNames>(flagNames: Array<T>): { [Key in T]: boolean };
function getFlagValues(flagNames?: Array<FeatureFlagNames>) {
    const selectedFlags = flagNames ? flagNames : flagKeys;
    return selectedFlags.reduce(
        (acc, flagName) => {
            acc[flagName] = flags[flagName].getValue();
            return acc;
        },
        {} as Record<FeatureFlagNames, boolean>,
    );
}

export const FeatureFlags = {
    FLAGS: flags,
    getFlagValues,
    createFlag,
};

/**
 * Get the current value of a feature flag.
 */
export const getFeatureFlag = (flagName: FeatureFlagNames) => flags[flagName].getValue();

export type { FeatureFlagNames };
